import React, { useState } from 'react';
import { css } from 'emotion';

const button = css`
    cursor: pointer;
    margin-left: 0.5em;
`

const errorText = css`
    color: #b00;
    margin-left: 0.5em;
`

// e.g. `attachment; filename="applog-1-2020-05-20-ios.log"`
const filenameInDisposition = /filename="([^"]+)"/

/**
 * The server names the file for us, but the browser only lets us read
 * Content-Disposition because the endpoint adds it to
 * Access-Control-Expose-Headers. Fall back if that ever stops being true.
 */
function filenameFrom(response: Response, fallback: string) {
    const disposition = response.headers.get('Content-Disposition')
    const matched = disposition && filenameInDisposition.exec(disposition)
    return matched ? matched[1] : fallback
}

/**
 * Downloads a log file.
 *
 * The endpoint needs a DiagToken header, so a plain <a href> can't do it -- we
 * have to fetch the bytes ourselves and hand the browser a blob. The bytes are
 * the same privacy-clipped stream the viewer shows; `download=1` only changes
 * the headers.
 */
export default function LogDownloadButton(
    { url, fallbackFilename = 'applog.log', label = 'Download' }:
    { url: string, fallbackFilename?: string, label?: string }
) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const download = async () => {
        setBusy(true);
        setError(null);
        let objectUrl: string | null = null;
        try {
            const response = await fetch(`${url}?download=1`, {
                headers: {
                    'Authorization': `DiagToken ${localStorage.getItem('diagtoken')}`
                }
            });
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            objectUrl = URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filenameFrom(response, fallbackFilename);
            link.click();
            // Revoking here looks right but isn't: "download the hyperlink" runs in
            // parallel per spec, and WebKit starts the download asynchronously after
            // a policy check. Revoke the URL in this task and Safari cancels the
            // download outright. Chrome/Firefox resolve the blob eagerly and
            // wouldn't need the delay, but it's harmless there. Same fix FileSaver.js
            // uses.
            const urlToRevoke = objectUrl;
            setTimeout(() => URL.revokeObjectURL(urlToRevoke), 60_000);
        } catch (e) {
            setError(String(e));
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button className={button} onClick={download} disabled={busy}>
                {busy ? 'Downloading…' : `⤓ ${label}`}
            </button>
            {error && <span className={errorText}>Download failed: {error} 🐞</span>}
        </>
    );
}
